import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert,
  Paper,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TableSortLabel
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { performanceService } from '../../services/performanceService';

// Color palettes for different series
const ACTION_COLORS = ['#D74B00', '#1A2E22', '#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#607d8b'];
const USER_LOAD_COLORS = ['#1976d2', '#2196f3', '#64b5f6', '#90caf9', '#bbdefb', '#e3f2fd'];

const TrendsTab = () => {
  // Tab state
  const [activeSubTab, setActiveSubTab] = useState(0); // 0 = Trends, 1 = Compare

  // State for filters (Trends tab)
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [actions, setActions] = useState([]);
  const [selectedActions, setSelectedActions] = useState([]);
  const [userLoads, setUserLoads] = useState([]);
  const [selectedUserLoad, setSelectedUserLoad] = useState('');

  // State for data and UI
  const [trendsData, setTrendsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [userLoadsLoading, setUserLoadsLoading] = useState(false);
  const [error, setError] = useState('');

  // State for Compare Executions tab
  const [allExecutions, setAllExecutions] = useState([]);
  const [execution1, setExecution1] = useState('');
  const [execution2, setExecution2] = useState('');
  const [execution1Data, setExecution1Data] = useState(null);
  const [execution2Data, setExecution2Data] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState('');
  const [comparisonData, setComparisonData] = useState([]);
  const [filterMode, setFilterMode] = useState('common'); // 'common', 'all', 'exec1Only', 'exec2Only'
  const [showDiffOnly, setShowDiffOnly] = useState(false); // Show only rows with differences in Common Only mode
  const [sortBy, setSortBy] = useState('name'); // 'name', 'avgDiff', 'p90Diff', 'p95Diff', 'p99Diff'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  const [expandedCompareRows, setExpandedCompareRows] = useState(new Set());

  // Load projects on component mount
  useEffect(() => {
    fetchProjects();
    fetchAllExecutions();
  }, []);

  // Reset "Show Diff Only" when switching away from "Common Only" mode
  useEffect(() => {
    if (filterMode !== 'common') {
      setShowDiffOnly(false);
    }
  }, [filterMode]);

  // Fetch all executions for comparison feature
  const fetchAllExecutions = async () => {
    try {
      const response = await performanceService.getExecutions({ status: 'completed' });
      setAllExecutions(response.data || []);
    } catch (err) {
      console.error('Error fetching all executions:', err);
    }
  };

  // Load actions when project changes
  useEffect(() => {
    if (selectedProject) {
      fetchActions();
      setSelectedActions([]);
      setSelectedUserLoad('');
      setTrendsData(null);
    } else {
      setActions([]);
      setSelectedActions([]);
      setUserLoads([]);
      setSelectedUserLoad('');
      setTrendsData(null);
    }
  }, [selectedProject]);

  // Load user loads when project or actions change
  useEffect(() => {
    if (selectedProject && selectedActions.length > 0) {
      fetchUserLoads();
      if (selectedActions.length > 1) {
        // Reset user load when multiple actions are selected
        setSelectedUserLoad('');
      }
    } else {
      setUserLoads([]);
      setSelectedUserLoad('');
    }
  }, [selectedProject, selectedActions]);

  const fetchProjects = async () => {
    try {
      setProjectsLoading(true);
      setError('');
      const response = await performanceService.getProjects();
      setProjects(response.data || []);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch projects';
      setError(`Failed to fetch projects: ${errorMessage}`);
      console.error('Error fetching projects:', err);
    } finally {
      setProjectsLoading(false);
    }
  };

  const fetchActions = async () => {
    try {
      setActionsLoading(true);
      const response = await performanceService.getProjectActions(selectedProject);
      setActions(response.data || []);
    } catch (err) {
      console.error('Error fetching actions:', err);
      setActions([]);
    } finally {
      setActionsLoading(false);
    }
  };

  const fetchUserLoads = async () => {
    try {
      setUserLoadsLoading(true);
      // Use first action for filtering user loads
      const firstAction = selectedActions.length > 0 ? selectedActions[0] : null;
      const response = await performanceService.getProjectUserLoads(selectedProject, firstAction);
      setUserLoads(response.data || []);
    } catch (err) {
      console.error('Error fetching user loads:', err);
      setUserLoads([]);
    } finally {
      setUserLoadsLoading(false);
    }
  };

  const fetchTrendsData = async () => {
    if (!selectedProject) {
      setError('Please select a Project to view trends');
      return;
    }

    if (selectedActions.length > 1 && !selectedUserLoad) {
      setError('User Load is required when multiple actions are selected');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const filters = {
        projectId: selectedProject
      };
      
      // Only add actions if they are selected
      if (selectedActions.length > 0) {
        filters.actions = selectedActions;
      }
      
      if (selectedUserLoad) {
        filters.userLoad = selectedUserLoad;
      }

      const response = await performanceService.getTrendsData(filters);
      setTrendsData(response.data);
      
      if (response.data.executions.length === 0) {
        setError('No execution data found for the selected filters');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch trends data';
      setError(`Failed to fetch trends data: ${errorMessage}`);
      console.error('Error fetching trends data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSelectedProject('');
    setSelectedActions([]);
    setSelectedUserLoad('');
    setTrendsData(null);
    setError('');
  };

  const handleActionsChange = (event) => {
    const value = event.target.value;
    setSelectedActions(typeof value === 'string' ? value.split(',') : value);
  };

  // Compare Executions functionality
  const handleCompareExecutions = async () => {
    if (!execution1 || !execution2) {
      setCompareError('Please select both executions to compare');
      return;
    }

    if (execution1 === execution2) {
      setCompareError('Please select two different executions');
      return;
    }

    try {
      setCompareLoading(true);
      setCompareError('');

      // Fetch label stats for both executions
      const [response1, response2] = await Promise.all([
        performanceService.getExecutionLabelStats(execution1),
        performanceService.getExecutionLabelStats(execution2)
      ]);

      const stats1 = response1.data || [];
      const stats2 = response2.data || [];

      setExecution1Data(stats1);
      setExecution2Data(stats2);

      // Calculate comparison data
      setExpandedCompareRows(new Set());
      calculateComparison(stats1, stats2);

    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch execution data';
      setCompareError(`Failed to compare executions: ${errorMessage}`);
      console.error('Error comparing executions:', err);
    } finally {
      setCompareLoading(false);
    }
  };

  const calculateComparison = (stats1, stats2) => {
    // Build maps keyed by normalized label so that actions differing only in instance
    // name (e.g. /modisheetman1/ vs /legperftest2/) are treated as the same request.
    // We keep the original Label from each stat for display purposes.
    const stats1Map = new Map(
      stats1
        .filter(s => s.Label)
        .map(s => [normalizeLabelForComparison(s.Label), s])
    );
    const stats2Map = new Map(
      stats2
        .filter(s => s.Label)
        .map(s => [normalizeLabelForComparison(s.Label), s])
    );

    // Union of all normalized keys
    const allNormalizedLabels = new Set([...stats1Map.keys(), ...stats2Map.keys()]);

    const comparison = [];
    allNormalizedLabels.forEach(normalizedLabel => {
      if (!normalizedLabel) return; // Skip empty labels

      const stat1 = stats1Map.get(normalizedLabel);
      const stat2 = stats2Map.get(normalizedLabel);

      if (stat1 || stat2) {
        // Display label: prefer exec1's original, fall back to exec2's
        const label = stat1?.Label || stat2?.Label || normalizedLabel;

        const item = {
          label,
          normalizedLabel,
          inExec1: !!stat1,
          inExec2: !!stat2,
          // Execution 1 values
          exec1Average: stat1?.Average || 0,
          exec1Percentile90: stat1?.['90% Line'] || 0,
          exec1Percentile95: stat1?.['95% Line'] || 0,
          exec1Percentile99: stat1?.['99% Line'] || 0,
          exec1Throughput: stat1?.Throughput || 0,
          exec1ErrorRate: parseFloat(stat1?.['Error %']) || 0,
          exec1Samples: stat1?.['# Samples'] || 0,
          // Execution 2 values
          exec2Average: stat2?.Average || 0,
          exec2Percentile90: stat2?.['90% Line'] || 0,
          exec2Percentile95: stat2?.['95% Line'] || 0,
          exec2Percentile99: stat2?.['99% Line'] || 0,
          exec2Throughput: stat2?.Throughput || 0,
          exec2ErrorRate: parseFloat(stat2?.['Error %']) || 0,
          exec2Samples: stat2?.['# Samples'] || 0,
        };

        // Calculate differences (negative means improvement for response times)
        item.avgDiff = item.exec2Average - item.exec1Average;
        item.avgDiffPct = item.exec1Average > 0 ? ((item.avgDiff / item.exec1Average) * 100) : 0;

        item.p90Diff = item.exec2Percentile90 - item.exec1Percentile90;
        item.p90DiffPct = item.exec1Percentile90 > 0 ? ((item.p90Diff / item.exec1Percentile90) * 100) : 0;

        item.p95Diff = item.exec2Percentile95 - item.exec1Percentile95;
        item.p95DiffPct = item.exec1Percentile95 > 0 ? ((item.p95Diff / item.exec1Percentile95) * 100) : 0;

        item.p99Diff = item.exec2Percentile99 - item.exec1Percentile99;
        item.p99DiffPct = item.exec1Percentile99 > 0 ? ((item.p99Diff / item.exec1Percentile99) * 100) : 0;

        // For throughput, positive is improvement
        item.throughputDiff = item.exec2Throughput - item.exec1Throughput;
        item.throughputDiffPct = item.exec1Throughput > 0 ? ((item.throughputDiff / item.exec1Throughput) * 100) : 0;

        item.errorRateDiff = item.exec2ErrorRate - item.exec1ErrorRate;

        comparison.push(item);
      }
    });

    setComparisonData(comparison);
  };

  const getFilteredComparisonData = () => {
    let filtered = [...comparisonData];

    // Apply filter mode
    switch (filterMode) {
      case 'common':
        filtered = filtered.filter(item => item.inExec1 && item.inExec2);

        // Apply "Show Diff Only" filter if enabled
        if (showDiffOnly) {
          const threshold = 0.1; // Same threshold as used in getDiffColor
          filtered = filtered.filter(item => {
            // Check if there's a meaningful difference in avg, P90, or P95
            const hasAvgDiff = Math.abs(item.avgDiff) >= threshold;
            const hasP90Diff = Math.abs(item.p90Diff) >= threshold;
            const hasP95Diff = Math.abs(item.p95Diff) >= threshold;
            return hasAvgDiff || hasP90Diff || hasP95Diff;
          });
        }
        break;
      case 'exec1Only':
        filtered = filtered.filter(item => item.inExec1 && !item.inExec2);
        break;
      case 'exec2Only':
        filtered = filtered.filter(item => !item.inExec1 && item.inExec2);
        break;
      case 'all':
      default:
        // No filtering
        break;
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'name':
          aVal = a.label.toLowerCase();
          bVal = b.label.toLowerCase();
          return sortOrder === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);

        case 'avgDiff':
          aVal = a.avgDiff;
          bVal = b.avgDiff;
          break;

        case 'p90Diff':
          aVal = a.p90Diff;
          bVal = b.p90Diff;
          break;

        case 'p95Diff':
          aVal = a.p95Diff;
          bVal = b.p95Diff;
          break;

        case 'p99Diff':
          aVal = a.p99Diff;
          bVal = b.p99Diff;
          break;

        case 'throughputDiff':
          aVal = a.throughputDiff;
          bVal = b.throughputDiff;
          break;

        default:
          return 0;
      }

      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return filtered;
  };

  const handleSortRequest = (field) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(field);
  };

  // Strips the instance name (first path segment) from action-level label segments so that
  // "1470 /modisheetman1/documentExtensionIcon.action" and
  // "1470 /legperftest2/documentExtensionIcon.action" are treated as the same request.
  const normalizeLabelForComparison = (label) => {
    const segments = label.split(' | ');
    const last = segments[segments.length - 1];
    // Pattern: "STATUS_CODE /instanceName/rest.action"
    const normalized = last.replace(/^(\d+)\s+\/[^/]+\/(.+)$/, '$1 /**/$2');
    return [...segments.slice(0, -1), normalized].join(' | ');
  };

  const toggleCompareRow = (label) => {
    setExpandedCompareRows(prev => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  // Organises filtered flat comparison data into parent rows with nested children.
  // A row is a child if its label minus the last segment matches an existing parent label.
  const buildHierarchicalComparison = (flatData) => {
    const labelSet = new Set(flatData.map(d => d.label));
    const childrenMap = new Map();
    const rootItems = [];

    flatData.forEach(item => {
      const segments = item.label.split(' | ');
      if (segments.length > 1) {
        const parentLabel = segments.slice(0, -1).join(' | ');
        if (labelSet.has(parentLabel)) {
          if (!childrenMap.has(parentLabel)) {
            childrenMap.set(parentLabel, []);
          }
          childrenMap.get(parentLabel).push({
            ...item,
            displayLabel: segments[segments.length - 1]
          });
          return;
        }
      }
      rootItems.push(item);
    });

    return { rootItems, childrenMap };
  };

  const getDiffColor = (diff, isInverse = false) => {
    // For response times: negative (decrease) is good (green), positive (increase) is bad (red)
    // For throughput (isInverse=true): positive (increase) is good (green), negative (decrease) is bad (red)
    const threshold = 0.1; // Consider <0.1ms as neutral

    if (Math.abs(diff) < threshold) return '#9e9e9e'; // Gray for negligible difference

    if (isInverse) {
      return diff > 0 ? '#4caf50' : '#f44336'; // Green for increase, red for decrease
    } else {
      return diff < 0 ? '#4caf50' : '#f44336'; // Green for decrease, red for increase
    }
  };

  const getDiffIcon = (diff, isInverse = false) => {
    const threshold = 0.1;
    if (Math.abs(diff) < threshold) return <RemoveIcon fontSize="small" />;

    if (isInverse) {
      return diff > 0 ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />;
    } else {
      return diff < 0 ? <TrendingDownIcon fontSize="small" /> : <TrendingUpIcon fontSize="small" />;
    }
  };

  const getExecutionLabel = (executionId) => {
    const exec = allExecutions.find(e => e._id === executionId);
    if (!exec) return '';
    return `${exec.projectId?.projectName || 'Unknown'} - ${exec.release || 'N/A'} - ${exec.build || 'N/A'} - ${exec.userLoad} users`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  };

  const getActionColor = (actionIndex) => {
    return ACTION_COLORS[actionIndex % ACTION_COLORS.length];
  };

  const getUserLoadColor = (userLoadIndex) => {
    return USER_LOAD_COLORS[userLoadIndex % USER_LOAD_COLORS.length];
  };

  const prepareChartData = () => {
    if (!trendsData || !trendsData.executions) return [];
    
    if (selectedActions.length === 1 && !selectedUserLoad) {
      // Single action, multiple user loads - create one data point per execution
      const action = selectedActions[0];
      
      // Create one data point for each execution, with all user loads represented
      return trendsData.executions.map(execution => {
        const dataPoint = {
          date: formatDate(execution.executionDate),
          fullDate: execution.executionDate,
          release: execution.release,
          build: execution.build,
          userLoad: execution.userLoad
        };

        // Add data for this specific user load if action exists
        if (execution.actionsStats[action]) {
          const stats = execution.actionsStats[action];
          const userLoad = execution.userLoad;
          
          // Set data for this specific user load
          dataPoint[`${action}_${userLoad}_averageResponseTime`] = stats.averageResponseTime;
          dataPoint[`${action}_${userLoad}_percentile90`] = stats.percentile90;
          dataPoint[`${action}_${userLoad}_percentile95`] = stats.percentile95;
          dataPoint[`${action}_${userLoad}_throughput`] = stats.throughput;
          dataPoint[`${action}_${userLoad}_errorRate`] = stats.errorRate;
        }

        return dataPoint;
      }).sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
    } else {
      // Standard case - single user load or multiple actions
      return trendsData.executions.map(execution => {
        const dataPoint = {
          date: formatDate(execution.executionDate),
          fullDate: execution.executionDate,
          release: execution.release,
          build: execution.build,
          userLoad: execution.userLoad
        };

        // Add data for each action
        Object.keys(execution.actionsStats).forEach(action => {
          const stats = execution.actionsStats[action];
          dataPoint[`${action}_averageResponseTime`] = stats.averageResponseTime;
          dataPoint[`${action}_percentile90`] = stats.percentile90;
          dataPoint[`${action}_percentile95`] = stats.percentile95;
          dataPoint[`${action}_throughput`] = stats.throughput;
          dataPoint[`${action}_errorRate`] = stats.errorRate;
        });

        return dataPoint;
      }).sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
    }
  };

  const generateChartSeries = (metricType) => {
    if (!trendsData) return [];

    const series = [];

    if (selectedActions.length === 1 && !selectedUserLoad) {
      // Single action, multiple user loads
      const action = selectedActions[0];
      const userLoads = trendsData.metadata.availableUserLoads || [];
      
      userLoads.forEach((userLoad, index) => {
        series.push({
          key: `${action}_${userLoad}_${metricType}`,
          dataKey: `${action}_${userLoad}_${metricType}`,
          name: `${action} (${userLoad} users)`,
          color: getUserLoadColor(index)
        });
      });
    } else if (selectedActions.length > 1 && selectedUserLoad) {
      // Multiple actions, single user load
      selectedActions.forEach((action, index) => {
        series.push({
          key: `${action}_${metricType}`,
          dataKey: `${action}_${metricType}`,
          name: action,
          color: getActionColor(index)
        });
      });
    } else {
      // Single action, single user load
      const action = selectedActions[0];
      series.push({
        key: `${action}_${metricType}`,
        dataKey: `${action}_${metricType}`,
        name: action,
        color: getActionColor(0)
      });
    }

    return series;
  };

  const filterChartData = (chartData, series) => {
    if (selectedActions.length === 1 && !selectedUserLoad) {
      // Filter data by user load for each series
      return series.map(seriesItem => {
        if (seriesItem.userLoad) {
          return {
            ...seriesItem,
            data: chartData.filter(item => item.userLoad === seriesItem.userLoad)
          };
        }
        return { ...seriesItem, data: chartData };
      });
    }
    
    return series.map(seriesItem => ({ ...seriesItem, data: chartData }));
  };

  // Function to prepare data for the cross-tabulation table
  const prepareCrossTabData = () => {
    if (!trendsData || !trendsData.executions || trendsData.executions.length === 0) {
      return { actions: [], userLoads: [], data: {}, errorRates: {} };
    }

    // Get unique actions and user loads
    const actions = [...new Set(trendsData.metadata.actions)].sort();
    const userLoads = [...new Set(trendsData.executions.map(exec => exec.userLoad))].sort((a, b) => a - b);
    
    // Initialize data structures
    const data = {};
    const errorRates = {};
    actions.forEach(action => {
      data[action] = {};
      errorRates[action] = {};
      userLoads.forEach(userLoad => {
        data[action][userLoad] = null;
        errorRates[action][userLoad] = null;
      });
    });

    // Group executions by user load and find the latest for each
    const latestExecutions = {};
    userLoads.forEach(userLoad => {
      const executionsForUserLoad = trendsData.executions.filter(exec => exec.userLoad === userLoad);
      if (executionsForUserLoad.length > 0) {
        // Sort by date descending and take the first one (latest)
        latestExecutions[userLoad] = executionsForUserLoad.sort((a, b) => 
          new Date(b.executionDate) - new Date(a.executionDate)
        )[0];
      }
    });

    // Fill in the data from latest executions
    Object.entries(latestExecutions).forEach(([userLoad, execution]) => {
      Object.entries(execution.actionsStats).forEach(([action, stats]) => {
        data[action][userLoad] = stats.percentile95;
        errorRates[action][userLoad] = parseFloat(stats.errorRate);
      });
    });

    return { actions, userLoads, data, errorRates };
  };

  const chartData = prepareChartData();
  const isMultipleUserLoads = selectedActions.length === 1 && !selectedUserLoad && trendsData?.metadata?.availableUserLoads?.length > 1;
  const isProjectOnlySelection = selectedProject && selectedActions.length === 0;
  const crossTabData = isProjectOnlySelection ? prepareCrossTabData() : null;

  // Validation state
  const isValidSelection = selectedProject && 
    (selectedActions.length === 0 || // Allow project-only selection
     (selectedActions.length === 1) || // Allow single action without user load
     (selectedActions.length > 1 && selectedUserLoad)); // Require user load for multiple actions

  return (
    <Box>
      {/* Sub-tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeSubTab} onChange={(e, newValue) => setActiveSubTab(newValue)}>
          <Tab label="Trends Analysis" />
          <Tab label="Compare Executions" />
        </Tabs>
      </Box>

      {/* Trends Analysis Tab */}
      {activeSubTab === 0 && (
        <Box>
          <Box mb={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6">
                Performance Trends Analysis
              </Typography>
              <Box display="flex" gap={1}>
                <Tooltip title="Clear all filters">
                  <IconButton onClick={handleClearFilters}>
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refresh projects">
                  <IconButton onClick={fetchProjects} disabled={projectsLoading}>
                    {projectsLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

        {/* Filter Panel */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={projectsLoading}>
                <InputLabel>Project *</InputLabel>
                <Select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  label="Project *"
                >
                  {projects.map((project) => (
                    <MenuItem key={project._id} value={project._id}>
                      {project.projectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={!selectedProject || actionsLoading}>
                <InputLabel>Actions</InputLabel>
                <Select
                  multiple
                  value={selectedActions}
                  onChange={handleActionsChange}
                  input={<OutlinedInput label="Actions" />}
                  renderValue={(selected) => {
                    if (selected.length === 0) return '';
                    if (selected.length === 1) return selected[0];
                    return `${selected.length} actions selected`;
                  }}
                >
                  {actions.map((action) => (
                    <MenuItem key={action} value={action}>
                      <Checkbox checked={selectedActions.indexOf(action) > -1} />
                      <ListItemText primary={action} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {actionsLoading && (
                <Box display="flex" justifyContent="center" mt={1}>
                  <CircularProgress size={16} />
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={selectedActions.length === 0 || userLoadsLoading}>
                <InputLabel>
                  User Load {selectedActions.length > 1 ? '*' : '(Optional)'}
                </InputLabel>
                <Select
                  value={selectedUserLoad}
                  onChange={(e) => setSelectedUserLoad(e.target.value)}
                  label={`User Load ${selectedActions.length > 1 ? '*' : '(Optional)'}`}
                >
                  {selectedActions.length === 1 && (
                    <MenuItem value="">All User Loads</MenuItem>
                  )}
                  {userLoads.map((userLoad) => (
                    <MenuItem key={userLoad} value={userLoad}>
                      {userLoad} users
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {userLoadsLoading && (
                <Box display="flex" justifyContent="center" mt={1}>
                  <CircularProgress size={16} />
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                onClick={fetchTrendsData}
                disabled={!isValidSelection || loading}
                fullWidth
                sx={{ height: 56 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Generate Trends'}
              </Button>
            </Grid>
          </Grid>

          {/* Validation Messages and Help Notes */}
          <Box mt={2}>
            {selectedActions.length > 1 && !selectedUserLoad && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                User Load selection is required when multiple actions are selected for comparison.
              </Alert>
            )}
            
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Tip:</strong> You can select only a Project (without selecting any Actions) to generate a cross-tabulation table showing 95th percentile response times for all actions and user loads.
              </Typography>
            </Alert>
          </Box>

          {/* Filter Summary */}
          {(selectedProject || selectedActions.length > 0 || selectedUserLoad) && (
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Current Selection:
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {selectedProject && (
                  <Chip 
                    label={`Project: ${projects.find(p => p._id === selectedProject)?.projectName || 'Unknown'}`}
                    size="small"
                    color="primary"
                  />
                )}
                {selectedActions.length > 0 && (
                  <Chip 
                    label={`Actions: ${selectedActions.join(', ')}`}
                    size="small"
                    color="primary"
                  />
                )}
                {selectedUserLoad && (
                  <Chip 
                    label={`User Load: ${selectedUserLoad} users`}
                    size="small"
                    color="secondary"
                  />
                )}
                {selectedActions.length === 1 && !selectedUserLoad && (
                  <Chip 
                    label="Comparing across all user loads"
                    size="small"
                    color="info"
                  />
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading trends data...
          </Typography>
        </Box>
      )}

      {/* Charts section - Only show when actions are selected */}
      {trendsData && chartData.length > 0 && !loading && !isProjectOnlySelection && (
        <Box>
          {/* Metadata - Simplified */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Trends Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2"><strong>Project:</strong> {trendsData.metadata.projectName}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2"><strong>Executions:</strong> {trendsData.metadata.totalExecutions}</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Charts Grid */}
          <Grid container spacing={3}>
            {/* Average Response Time */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Average Response Time Trend
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip 
                          formatter={(value, name) => [`${value} ms`, name]}
                          labelFormatter={(label) => `Date: ${label}`}
                        />
                        {generateChartSeries('averageResponseTime').map((series) => (
                          <Line
                            key={series.key}
                            type="monotone"
                            dataKey={series.dataKey}
                            stroke={series.color}
                            strokeWidth={2}
                            name={series.name}
                            connectNulls={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Percentiles */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    90th Percentile Response Time
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip 
                          formatter={(value, name) => [`${value} ms`, name]}
                          labelFormatter={(label) => `Date: ${label}`}
                        />
                        {generateChartSeries('percentile90').map((series) => (
                          <Line
                            key={series.key}
                            type="monotone"
                            dataKey={series.dataKey}
                            stroke={series.color}
                            strokeWidth={2}
                            name={series.name}
                            connectNulls={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Throughput */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Throughput Trend
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip 
                          formatter={(value, name) => [`${value} req/s`, name]}
                          labelFormatter={(label) => `Date: ${label}`}
                        />
                        {generateChartSeries('throughput').map((series) => (
                          <Line
                            key={series.key}
                            type="monotone"
                            dataKey={series.dataKey}
                            stroke={series.color}
                            strokeWidth={2}
                            name={series.name}
                            connectNulls={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Error Rate */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Error Rate Trend
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip 
                          formatter={(value, name) => [`${value}%`, name]}
                          labelFormatter={(label) => `Date: ${label}`}
                        />
                        {generateChartSeries('errorRate').map((series) => (
                          <Line
                            key={series.key}
                            type="monotone"
                            dataKey={series.dataKey}
                            stroke={series.color}
                            strokeWidth={2}
                            name={series.name}
                            connectNulls={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Shared Legend */}
          <Paper sx={{ p: 2, mt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ width: '100%', height: 50 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <Legend 
                    layout="horizontal" 
                    verticalAlign="middle" 
                    align="center"
                    wrapperStyle={{ paddingTop: 10 }}
                  />
                  {generateChartSeries('averageResponseTime').map((series) => (
                    <Line
                      key={series.key}
                      type="monotone"
                      dataKey={series.dataKey}
                      stroke={series.color}
                      name={series.name}
                      hide={true}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Cross-tabulation table for project-only selection */}
      {isProjectOnlySelection && crossTabData && crossTabData.actions.length > 0 && !loading && (
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            95th Percentile Response Times by Action and User Load
          </Typography>
          <Box mb={2}>
            <Typography variant="caption" color="textSecondary">
              Data shown from latest execution at each user load. Color coding based on error rates:
              <Box component="span" sx={{ color: '#4caf50', ml: 1, fontWeight: 'bold' }}>Green (&lt;2%)</Box>,
              <Box component="span" sx={{ color: '#ff9800', ml: 1, fontWeight: 'bold' }}>Amber (2-5%)</Box>,
              <Box component="span" sx={{ color: '#f44336', ml: 1, fontWeight: 'bold' }}>Red (&gt;5%)</Box>
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                  {crossTabData.userLoads.map(userLoad => (
                    <TableCell key={userLoad} align="right" sx={{ fontWeight: 'bold' }}>
                      {userLoad} Users
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {crossTabData.actions.map((action, index) => (
                  <TableRow key={action} sx={{ 
                    backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'inherit',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                  }}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'medium' }}>
                      {action}
                    </TableCell>
                    {crossTabData.userLoads.map(userLoad => {
                      const value = crossTabData.data[action][userLoad];
                      const errorRate = crossTabData.errorRates[action][userLoad];
                      
                      // Color coding based on error rate
                      let color = 'inherit';
                      if (errorRate !== null) {
                        if (errorRate < 2) color = '#4caf50'; // Green for < 2%
                        else if (errorRate < 5) color = '#ff9800'; // Amber for 2-5%
                        else color = '#f44336'; // Red for > 5%
                      }
                      
                      return (
                        <TableCell key={`${action}-${userLoad}`} align="right" sx={{ color }}>
                          {value !== null ? `${Math.round(value)} ms` : '-'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {trendsData && chartData.length === 0 && !loading && !isProjectOnlySelection && (
        <Alert severity="info">
          No trend data available for the selected filters. Please ensure there are completed executions with the selected actions.
        </Alert>
      )}
        </Box>
      )}

      {/* Compare Executions Tab */}
      {activeSubTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Compare Two Executions
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Select any two executions from any project to compare their performance metrics side-by-side.
            This is useful for comparing legacy vs modern implementations or different versions.
          </Typography>

          {/* Execution Selectors */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={5}>
                <FormControl fullWidth>
                  <InputLabel>Execution 1</InputLabel>
                  <Select
                    value={execution1}
                    onChange={(e) => setExecution1(e.target.value)}
                    label="Execution 1"
                  >
                    {allExecutions.map((exec) => (
                      <MenuItem key={exec._id} value={exec._id}>
                        {exec.projectId?.projectName || 'Unknown'} - {exec.release || 'N/A'} - {exec.build || 'N/A'} - {exec.userLoad} users
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={5}>
                <FormControl fullWidth>
                  <InputLabel>Execution 2</InputLabel>
                  <Select
                    value={execution2}
                    onChange={(e) => setExecution2(e.target.value)}
                    label="Execution 2"
                  >
                    {allExecutions.map((exec) => (
                      <MenuItem key={exec._id} value={exec._id}>
                        {exec.projectId?.projectName || 'Unknown'} - {exec.release || 'N/A'} - {exec.build || 'N/A'} - {exec.userLoad} users
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
                  onClick={handleCompareExecutions}
                  disabled={!execution1 || !execution2 || compareLoading}
                  fullWidth
                  sx={{ height: 56 }}
                >
                  {compareLoading ? <CircularProgress size={24} /> : 'Compare'}
                </Button>
              </Grid>
            </Grid>

            {compareError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {compareError}
              </Alert>
            )}
          </Paper>

          {/* Comparison Results */}
          {comparisonData.length > 0 && (
            <Box>
              {/* Filter and Sort Controls */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                  <Box display="flex" gap={1} alignItems="center">
                    <Typography variant="body2" sx={{ mr: 2, alignSelf: 'center' }}>
                      <strong>Filter:</strong>
                    </Typography>
                    <Button
                      size="small"
                      variant={filterMode === 'common' ? 'contained' : 'outlined'}
                      onClick={() => setFilterMode('common')}
                    >
                      Common Only
                    </Button>
                    <Button
                      size="small"
                      variant={filterMode === 'all' ? 'contained' : 'outlined'}
                      onClick={() => setFilterMode('all')}
                    >
                      All Requests
                    </Button>
                    <Button
                      size="small"
                      variant={filterMode === 'exec1Only' ? 'contained' : 'outlined'}
                      onClick={() => setFilterMode('exec1Only')}
                    >
                      Exec 1 Only
                    </Button>
                    <Button
                      size="small"
                      variant={filterMode === 'exec2Only' ? 'contained' : 'outlined'}
                      onClick={() => setFilterMode('exec2Only')}
                    >
                      Exec 2 Only
                    </Button>

                    {/* Show Diff Only checkbox - only visible for Common Only mode */}
                    {filterMode === 'common' && (
                      <Box display="flex" alignItems="center" sx={{ ml: 2, pl: 2, borderLeft: '1px solid #ddd' }}>
                        <Checkbox
                          checked={showDiffOnly}
                          onChange={(e) => setShowDiffOnly(e.target.checked)}
                          size="small"
                        />
                        <Typography variant="body2">
                          Show Diff Only
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {buildHierarchicalComparison(getFilteredComparisonData()).rootItems.length} requests shown
                  </Typography>
                </Box>
              </Paper>

              {/* Comparison Table */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Performance Comparison
                </Typography>
                <Box mb={2}>
                  <Typography variant="caption" color="textSecondary">
                    <strong>Execution 1:</strong> {getExecutionLabel(execution1)}
                  </Typography>
                  <br />
                  <Typography variant="caption" color="textSecondary">
                    <strong>Execution 2:</strong> {getExecutionLabel(execution2)}
                  </Typography>
                  <br />
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Color coding:
                    <Box component="span" sx={{ color: '#4caf50', ml: 1, fontWeight: 'bold' }}>Green = Improvement</Box>,
                    <Box component="span" sx={{ color: '#f44336', ml: 1, fontWeight: 'bold' }}>Red = Regression</Box>,
                    <Box component="span" sx={{ color: '#9e9e9e', ml: 1, fontWeight: 'bold' }}>Gray = Negligible</Box>
                  </Typography>
                </Box>

                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 1200 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={sortBy === 'name'}
                            direction={sortBy === 'name' ? sortOrder : 'asc'}
                            onClick={() => handleSortRequest('name')}
                          >
                            Request Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">Exec 1 Avg (ms)</TableCell>
                        <TableCell align="right">Exec 2 Avg (ms)</TableCell>
                        <TableCell align="center">
                          <TableSortLabel
                            active={sortBy === 'avgDiff'}
                            direction={sortBy === 'avgDiff' ? sortOrder : 'asc'}
                            onClick={() => handleSortRequest('avgDiff')}
                          >
                            Avg Diff
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">Exec 1 P90 (ms)</TableCell>
                        <TableCell align="right">Exec 2 P90 (ms)</TableCell>
                        <TableCell align="center">
                          <TableSortLabel
                            active={sortBy === 'p90Diff'}
                            direction={sortBy === 'p90Diff' ? sortOrder : 'asc'}
                            onClick={() => handleSortRequest('p90Diff')}
                          >
                            P90 Diff
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">Exec 1 P95 (ms)</TableCell>
                        <TableCell align="right">Exec 2 P95 (ms)</TableCell>
                        <TableCell align="center">
                          <TableSortLabel
                            active={sortBy === 'p95Diff'}
                            direction={sortBy === 'p95Diff' ? sortOrder : 'asc'}
                            onClick={() => handleSortRequest('p95Diff')}
                          >
                            P95 Diff
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const { rootItems, childrenMap } = buildHierarchicalComparison(getFilteredComparisonData());

                        const renderMetricCells = (row) => (
                          <>
                            <TableCell align="right">{row.inExec1 ? row.exec1Average.toFixed(2) : '-'}</TableCell>
                            <TableCell align="right">{row.inExec2 ? row.exec2Average.toFixed(2) : '-'}</TableCell>
                            <TableCell align="center">
                              {row.inExec1 && row.inExec2 ? (
                                <Box display="flex" alignItems="center" justifyContent="center" sx={{ color: getDiffColor(row.avgDiff) }}>
                                  {getDiffIcon(row.avgDiff)}
                                  <Box component="span" sx={{ ml: 0.5 }}>
                                    {row.avgDiff > 0 ? '+' : ''}{row.avgDiff.toFixed(2)} ms ({row.avgDiffPct > 0 ? '+' : ''}{row.avgDiffPct.toFixed(1)}%)
                                  </Box>
                                </Box>
                              ) : '-'}
                            </TableCell>
                            <TableCell align="right">{row.inExec1 ? row.exec1Percentile90.toFixed(2) : '-'}</TableCell>
                            <TableCell align="right">{row.inExec2 ? row.exec2Percentile90.toFixed(2) : '-'}</TableCell>
                            <TableCell align="center">
                              {row.inExec1 && row.inExec2 ? (
                                <Box display="flex" alignItems="center" justifyContent="center" sx={{ color: getDiffColor(row.p90Diff) }}>
                                  {getDiffIcon(row.p90Diff)}
                                  <Box component="span" sx={{ ml: 0.5 }}>
                                    {row.p90Diff > 0 ? '+' : ''}{row.p90Diff.toFixed(2)} ms ({row.p90DiffPct > 0 ? '+' : ''}{row.p90DiffPct.toFixed(1)}%)
                                  </Box>
                                </Box>
                              ) : '-'}
                            </TableCell>
                            <TableCell align="right">{row.inExec1 ? row.exec1Percentile95.toFixed(2) : '-'}</TableCell>
                            <TableCell align="right">{row.inExec2 ? row.exec2Percentile95.toFixed(2) : '-'}</TableCell>
                            <TableCell align="center">
                              {row.inExec1 && row.inExec2 ? (
                                <Box display="flex" alignItems="center" justifyContent="center" sx={{ color: getDiffColor(row.p95Diff) }}>
                                  {getDiffIcon(row.p95Diff)}
                                  <Box component="span" sx={{ ml: 0.5 }}>
                                    {row.p95Diff > 0 ? '+' : ''}{row.p95Diff.toFixed(2)} ms ({row.p95DiffPct > 0 ? '+' : ''}{row.p95DiffPct.toFixed(1)}%)
                                  </Box>
                                </Box>
                              ) : '-'}
                            </TableCell>
                          </>
                        );

                        return rootItems.map((row) => {
                          const children = childrenMap.get(row.label) || [];
                          const hasChildren = children.length > 0;
                          const isExpanded = expandedCompareRows.has(row.label);

                          return (
                            <React.Fragment key={row.label}>
                              {/* Parent / transaction row */}
                              <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}>
                                <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                  <Box display="flex" alignItems="center">
                                    {hasChildren ? (
                                      <IconButton size="small" onClick={() => toggleCompareRow(row.label)} sx={{ mr: 0.5, p: 0.25 }}>
                                        {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                      </IconButton>
                                    ) : (
                                      <Box sx={{ width: 28 }} />
                                    )}
                                    {row.label}
                                    {!row.inExec1 && <Chip label="Exec 2 Only" size="small" sx={{ ml: 1 }} />}
                                    {!row.inExec2 && <Chip label="Exec 1 Only" size="small" sx={{ ml: 1 }} />}
                                  </Box>
                                </TableCell>
                                {renderMetricCells(row)}
                              </TableRow>

                              {/* Child / action rows — shown only when parent is expanded */}
                              {isExpanded && children.map((child) => (
                                <TableRow
                                  key={child.label}
                                  sx={{
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)' }
                                  }}
                                >
                                  <TableCell sx={{ pl: 6 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'text.primary' }}>
                                      {child.displayLabel}
                                    </Typography>
                                    {!child.inExec1 && <Chip label="Exec 2 Only" size="small" sx={{ ml: 1 }} />}
                                    {!child.inExec2 && <Chip label="Exec 1 Only" size="small" sx={{ ml: 1 }} />}
                                  </TableCell>
                                  {renderMetricCells(child)}
                                </TableRow>
                              ))}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>

                {buildHierarchicalComparison(getFilteredComparisonData()).rootItems.length === 0 && (
                  <Box py={3} textAlign="center">
                    <Typography variant="body2" color="textSecondary">
                      No requests match the selected filter.
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )}

          {!compareLoading && comparisonData.length === 0 && execution1 && execution2 && (
            <Alert severity="info">
              Click "Compare" to view the performance comparison between the selected executions.
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TrendsTab;
